import { useEffect, useRef, useState } from "react";
import { OFFER } from "../config/offer.config";
import { countyForZip } from "../config/serviceArea.config";
import { submitLead, readUtm, newEventId } from "../lib/lead";
import {
  trackLead,
  trackViewContent,
  trackLeadFormStarted,
  getCookie,
} from "../lib/pixel";
import BookingStep from "../components/BookingStep";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Simple lead form (baja fricción, máximo volumen). Pide lo mínimo: nombre,
 * teléfono, email, zip, qué baño + consent. Scope/banda/rango van null — Sofia
 * los completa en la llamada (payload contract).
 */
export default function SimpleForm() {
  const [f, setF] = useState({
    first_name: "",
    phone: "",
    email: "",
    zip: "",
    want: "",
    consent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // COLD — vio el form (llegó del ad).
  useEffect(() => trackViewContent("simple_form"), []);

  // WARM — LeadFormStarted una sola vez en la primera edición.
  const started = useRef(false);
  const set = (k: keyof typeof f, v: string | boolean) => {
    if (!started.current) {
      started.current = true;
      trackLeadFormStarted("simple_form");
    }
    setF((s) => ({ ...s, [k]: v }));
  };

  function validate() {
    const e: Record<string, string> = {};
    if (!f.first_name.trim()) e.first_name = "Tell us your name.";
    if (!/\d{10,}/.test(f.phone.replace(/\D/g, ""))) e.phone = "A valid phone, please.";
    if (!EMAIL_RE.test(f.email)) e.email = "A valid email, please.";
    if (!countyForZip(f.zip)) e.zip = "We serve Pinellas, Hillsborough and Pasco.";
    if (!f.consent) e.consent = "Please agree to be contacted.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setError(null);
    const eventId = newEventId();
    const res = await submitLead({
      lead_source: "simple_form",
      first_name: f.first_name,
      phone: f.phone,
      email: f.email,
      address: "",
      zip: f.zip,
      // Simple form: scope va null (Sofia lo completa en la llamada). El texto libre
      // "want" queda como intención — Sofia lo confirma; no forzamos el contrato.
      bathroom_scope: null,
      investment_band: null,
      estimator_range: null,
      timeline: null,
      consent: f.consent,
      utm: readUtm(),
      event_id: eventId,
      fbp: getCookie("_fbp"),
      fbc: getCookie("_fbc"),
      page_url: typeof window !== "undefined" ? window.location.href : undefined,
    });
    setSubmitting(false);
    if (res.ok) {
      trackLead(eventId);
      setDone(true);
    } else setError("Something went wrong. Please try again.");
  }

  if (done) {
    return (
      <main className="mx-auto max-w-md px-6 py-20">
        <h1 className="font-serif text-4xl text-ink">Thank you.</h1>
        <p className="mt-4 text-stone">
          Book your design consultation below — or a designer will reach out shortly.
        </p>
        <BookingStep headline="Book your design consultation" />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-md px-6 py-16">
      <p className="font-sans text-xs uppercase tracking-[0.2em] text-clay">
        A Couple &amp; A Dream · {OFFER.serviceArea.join(", ")}
      </p>
      <h1 className="mt-3 font-serif text-4xl text-ink">Start your transformation</h1>
      <p className="mt-3 text-stone">
        Tell us a little about the bathroom you want to transform — a designer will
        take it from there.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
        <Input label="First name" v={f.first_name} on={(v) => set("first_name", v)} err={errors.first_name} />
        <Input label="Phone" v={f.phone} on={(v) => set("phone", v)} err={errors.phone} />
        <Input label="Email" v={f.email} on={(v) => set("email", v)} err={errors.email} />
        <Input
          label="ZIP"
          v={f.zip}
          on={(v) => set("zip", v)}
          err={errors.zip}
          hint={countyForZip(f.zip) ? `${countyForZip(f.zip)} County ✓` : undefined}
        />
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wide text-stone">
            Which bathroom do you want to transform?
          </span>
          <textarea
            className="acad-input min-h-[80px]"
            value={f.want}
            onChange={(e) => set("want", e.target.value)}
          />
        </label>
        <label className="flex items-start gap-3 text-sm text-stone">
          <input
            type="checkbox"
            className="mt-1"
            checked={f.consent}
            onChange={(e) => set("consent", e.target.checked)}
          />
          <span>
            I agree that A Couple &amp; A Dream may contact me by call, text and email.
            Message/data rates may apply.
          </span>
        </label>
        {errors.consent && <p className="text-sm text-clay">{errors.consent}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-ink px-6 py-3 font-sans text-sm uppercase tracking-widest text-bone transition hover:bg-clay disabled:opacity-60"
        >
          {submitting ? "Sending…" : "Request my consultation →"}
        </button>
        {error && <p className="text-sm text-clay">{error}</p>}
      </form>
    </main>
  );
}

function Input({
  label,
  v,
  on,
  err,
  hint,
}: {
  label: string;
  v: string;
  on: (v: string) => void;
  err?: string;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center justify-between text-xs uppercase tracking-wide text-stone">
        {label}
        {hint && <span className="text-sage normal-case tracking-normal">{hint}</span>}
      </span>
      <input className="acad-input" value={v} onChange={(e) => on(e.target.value)} />
      {err && <span className="mt-1 block text-xs text-clay">{err}</span>}
    </label>
  );
}
